// 汎用ユーティリティ (旧 utils/mixins.js から移植 — 純粋関数のみなので IIFE)
;(function () {
  const mixinUtils = {
    async blobUrlToImageData(blobUrl, maxsize = 1024) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = (err) => reject(err);
        img.src = blobUrl;
      }).then((img) => {
        URL.revokeObjectURL(blobUrl);
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        while (width > maxsize || height > maxsize) {
          width = Math.floor(width / 2);
          height = Math.floor(height / 2);
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        return ctx.getImageData(0, 0, width, height);
      });
    },

    async reduceBlobUrl(blobUrl, maxsize = 1024) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = (err) => reject(err);
        img.src = blobUrl;
      }).then((img) => {
        let width = img.width;
        let height = img.height;
        while (width > maxsize || height > maxsize) {
          width = Math.floor(width / 2);
          height = Math.floor(height / 2);
        }
        URL.revokeObjectURL(blobUrl);
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        return canvas.toDataURL('image/jpeg', 0.85);
      });
    },

    async rotateBlobUrl(blobUrl, rotate = 90) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = (err) => reject(err);
        img.src = blobUrl;
      }).then((img) => {
        URL.revokeObjectURL(blobUrl);
        const canvas = document.createElement('canvas');
        if (rotate && rotate > 0) {
          if (rotate === 180) {
            canvas.width = img.width;
            canvas.height = img.height;
          } else {
            canvas.width = img.height;
            canvas.height = img.width;
          }
          const ctx = canvas.getContext('2d');
          ctx.rotate((rotate * Math.PI) / 180);
          if (rotate === 90) ctx.translate(0, -img.height);
          else if (rotate === 180) ctx.translate(-img.width, -img.height);
          else if (rotate === 270) ctx.translate(-img.width, 0);
          ctx.drawImage(img, 0, 0, img.width, img.height);
          return canvas.toDataURL();
        }
        console.error('rotateBlobUrl() の角度指定が不正です (90, 180, 270 のみ可)');
        return blobUrl;
      });
    },

    /**
     * 上品な alertdialog (デジタル庁指針 + WAI-ARIA APG dialog pattern 準拠)
     *  - role="alertdialog" aria-modal="true"
     *  - aria-labelledby / aria-describedby で関連付け
     *  - 最初のボタンに autofocus、Tab で focus trap
     *  - Esc で「キャンセル相当 = 最後 (左) のボタン」を返す (空配列なら resolve(null))
     *  - 開く前の focus 要素を覚え、閉じる時に復元
     *  - prefers-reduced-motion は CSS 側でアニメ抑制
     */
    async presentDialog(sHeader, sSubHeader, sMsg, arrButns = ['いいえ', 'はい']) {
      return new Promise((resolve) => {
        const previouslyFocused = document.activeElement;
        const uid = 'dlg-' + Math.random().toString(36).slice(2, 8);
        const titleId = uid + '-title';
        const descId = uid + '-desc';

        const backdrop = document.createElement('div');
        backdrop.className = 'fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4';
        backdrop.style.animation = 'fadeIn 0.18s ease-out';

        const dialog = document.createElement('div');
        dialog.setAttribute('role', 'alertdialog');
        dialog.setAttribute('aria-modal', 'true');
        if (sHeader) dialog.setAttribute('aria-labelledby', titleId);
        if (sMsg || sSubHeader) dialog.setAttribute('aria-describedby', descId);
        dialog.className = 'bg-white rounded-lg shadow-card-hover max-w-md w-full p-6 border border-q4cl-border focus:outline-none';
        dialog.setAttribute('tabindex', '-1');
        dialog.style.animation = 'slideUp 0.22s ease-out';

        let html = '';
        if (sHeader) html += `<h2 id="${titleId}" class="text-xl font-bold text-q4cl-primary mb-2 leading-snug">${sHeader}</h2>`;
        if (sSubHeader || sMsg) {
          html += `<div id="${descId}" class="text-q4cl-ink mb-6 whitespace-pre-line leading-relaxed">`;
          if (sSubHeader) html += `<div class="text-base font-medium text-q4cl-muted mb-2">${sSubHeader}</div>`;
          if (sMsg) html += `<div class="text-sm">${sMsg}</div>`;
          html += `</div>`;
        }

        const buttons = arrButns
          .map((btnText, index) => {
            const isPrimary = index === arrButns.length - 1;
            const cls = isPrimary ? 'btn-primary' : 'btn-secondary';
            const safeText = String(btnText).replace(/"/g, '&quot;');
            return `<button type="button" class="${cls}" data-value="${safeText}">${safeText}</button>`;
          })
          .join(' ');
        html += `<div class="flex justify-end gap-2 flex-wrap">${buttons}</div>`;
        dialog.innerHTML = html;

        backdrop.appendChild(dialog);
        document.body.appendChild(backdrop);

        const finishWith = (value) => {
          document.removeEventListener('keydown', onKey, true);
          backdrop.remove();
          // 開く前の focus 位置を復元
          if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
            try { previouslyFocused.focus({ preventScroll: true }); } catch (e) { /* ignore */ }
          }
          resolve(value);
        };

        // ボタンクリック
        const btnNodes = Array.from(dialog.querySelectorAll('button'));
        btnNodes.forEach((btn) => {
          btn.addEventListener('click', () => finishWith(btn.getAttribute('data-value')));
        });

        // Esc キャンセル + Tab で focus trap
        const onKey = (e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            // ボタンが 1 つしかない (OK のみ) → そのボタンを返す
            // 2 つ以上 → 左端 (最も優先度低い = キャンセル相当) を返す
            const fallback = btnNodes.length === 1 ? btnNodes[0] : btnNodes[0];
            finishWith(fallback ? fallback.getAttribute('data-value') : null);
            return;
          }
          if (e.key === 'Tab' && btnNodes.length > 0) {
            const focusables = btnNodes;
            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            const active = document.activeElement;
            if (e.shiftKey) {
              if (active === first || !dialog.contains(active)) {
                e.preventDefault();
                last.focus();
              }
            } else {
              if (active === last) {
                e.preventDefault();
                first.focus();
              }
            }
          }
        };
        document.addEventListener('keydown', onKey, true);

        // 初期 focus: 右端 (primary / Enter で確定する想定のもの)
        setTimeout(() => {
          const target = btnNodes[btnNodes.length - 1] || dialog;
          try { target.focus({ preventScroll: true }); } catch (e) { /* ignore */ }
        }, 0);
      });
    },

    /**
     * Firebase RTDB のキーで使えない文字をパーセントエスケープ
     * 旧 Q4CL-console と同一仕様 (encodeURIComponent ではない)
     */
    escapeFbRtdbKey(s) {
      return s
        ? s
            .replace(/\./g, '%2E')
            .replace(/\$/g, '%24')
            .replace(/\[/g, '%5B')
            .replace(/\]/g, '%5D')
            .replace(/#/g, '%23')
            .replace(/\//g, '%2F')
        : s;
    },

    unescapeFbRtdbKey(s) {
      return s
        ? s
            .replace(/%2E/g, '.')
            .replace(/%24/g, '$')
            .replace(/%5B/g, '[')
            .replace(/%5D/g, ']')
            .replace(/%23/g, '#')
            .replace(/%2F/g, '/')
        : s;
    },

    /**
     * 和暦表示 (例: "(令和6年)")
     */
    warekiYear(date, format) {
      if (!date) return '';
      if (!format || format.indexOf('YYYY') < 0) return '';
      const year = Number(moment(date).format('YYYY'));
      const eras = [
        { year: 2018, name: '令和' },
        { year: 1988, name: '平成' },
        { year: 1925, name: '昭和' },
        { year: 1911, name: '大正' },
        { year: 1867, name: '明治' }
      ];
      for (const era of eras) {
        if (year > era.year) {
          if (year - era.year === 1) return '&emsp;(' + era.name + '元年)';
          return '&emsp;(' + era.name + (year - era.year) + '年)';
        }
      }
      return '';
    },

    /**
     * UUID v4 生成 (deviceId 用)
     */
    uuidv4() {
      return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
    }
  };

  window.mixinUtils = mixinUtils;
})();
